import React, { useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { cn } from 'src/utils/className';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  showClose?: boolean;
  closeButtonMessage?: string;
  title: string | React.ReactNode;
  children: React.ReactNode;
  actionButton?: React.ReactNode;
  className?: string;
};

export const Modal = ({
  isOpen,
  onClose,
  showClose = true,
  closeButtonMessage,
  title,
  children,
  actionButton,
  className,
}: ModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-active');
    } else {
      document.body.classList.remove('modal-active');
    }

    return () => document.body.classList.remove('modal-active');
  }, [isOpen]);

  const handleClose = (evt: React.MouseEvent | KeyboardEvent) => {
    if (!showClose) return;
    evt.stopPropagation();
    if (onClose) onClose();
  };

  const handleOverlayClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (!showClose) return;
    e.stopPropagation();
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) {
      handleClose(e);
    }
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if ((e.key === 'Escape' || e.key === 'Esc') && isOpen) {
        handleClose(e);
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  return (
    <div
      className={cn(
        `modal fixed w-full h-full top-0 left-0 flex items-center justify-center z-50`,
        isOpen
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none',
      )}
      onClick={handleOverlayClick}
    >
      <div className="modal-overlay absolute w-full h-full bg-neutral-700 opacity-50"></div>

      <div
        className={cn(
          'modal-container bg-neutral-0 w-11/12 lg:max-w-[60%] mx-auto rounded shadow-lg z-50 overflow-y-auto',
          className,
        )}
      >
        <div className="modal-content p-4 text-left">
          <div className="flex justify-between items-center pb-1">
            <div className="text-2xl font-medium">{title}</div>
            {showClose && (
              <IconButton
                onClick={handleClose}
                aria-label="Close modal"
                className="ml-4"
              >
                <X className="fill-current text-neutral-700" size={22} />
              </IconButton>
            )}
          </div>

          <div className="overflow-auto max-h-[80vh] p-1">{children}</div>

          <div className="flex justify-end pt-2">
            {showClose && (
              <Button onClick={handleClose} variant="outlined" className="mr-1">
                {closeButtonMessage || 'Close'}
              </Button>
            )}

            {actionButton}
          </div>
        </div>
      </div>
    </div>
  );
};
